/**
 * CREATIVE DETONATOR - EMPTY STATE COMPONENT
 * Unified empty state rendering across all modules
 *
 * Features:
 * - Preset configurations for common empty states
 * - Three variants: default, retro, minimal
 * - CTA button support with icons
 * - Consistent styling via CSS classes (no inline styles)
 *
 * @version 1
 */

// ============================================================================
// PRESET CONFIGURATIONS
// ============================================================================

export const EMPTY_STATE_PRESETS = {
  gallery: {
    icon: '&#127912;',  // Art palette
    title: 'No media yet',
    message: 'Generate images and videos from your ideas to see them here!',
    ctaText: 'Browse Ideas',
    ctaHref: 'ideas.html',
    ctaIcon: '&#128161;',
    variant: 'default'
  },

  ideas: {
    icon: '&#128161;',  // Lightbulb
    title: 'No ideas yet',
    message: 'Start a creative session to generate your first ideas!',
    ctaText: 'START SESSION',
    ctaHref: '/features/generator/index.html',
    variant: 'retro'
  },

  sessions: {
    icon: '&#128197;',  // Calendar
    title: 'No sessions yet',
    message: 'Start your first creative session to see it here!',
    ctaText: 'Start Session',
    ctaHref: '/features/generator/index.html',
    ctaIcon: '&#9889;',
    variant: 'default'
  },

  brands: {
    icon: '&#128736;',  // Wrench
    title: 'No brands yet',
    message: 'Create your first brand cartridge to get started.',
    ctaText: 'Create Brand',
    variant: 'minimal'
  },

  videos: {
    icon: '&#127909;',  // Camera
    title: 'No videos yet',
    message: 'Generate a storyboard to create videos.',
    variant: 'retro'
  },

  versionHistory: {
    icon: '&#128338;',  // Clock
    title: 'No versions',
    message: 'Edit this idea to create version history.',
    variant: 'minimal'
  },

  search: {
    icon: '&#128270;',  // Magnifying glass
    title: 'No results',
    message: 'Try a different search term or clear filters.',
    variant: 'minimal'
  }
};

// ============================================================================
// RENDER FUNCTIONS
// ============================================================================

/**
 * Render an empty state component
 * @param {Object} options - Configuration
 * @param {string} options.icon - HTML entity or emoji for icon
 * @param {string} options.title - Title text
 * @param {string} options.message - Message text
 * @param {string} options.ctaText - CTA button text (optional)
 * @param {string} options.ctaHref - CTA button href (optional)
 * @param {string} options.ctaIcon - CTA button icon (optional)
 * @param {string} options.variant - 'default' | 'retro' | 'minimal'
 * @param {string} options.className - Additional CSS class
 * @returns {string} - HTML string
 */
export function renderEmptyState({
  icon = '&#128161;',
  title = 'Nothing here yet',
  message = 'Get started by adding your first item.',
  ctaText = null,
  ctaHref = null,
  ctaIcon = null,
  variant = 'default',
  className = ''
}) {
  const variantClass = `empty-state--${variant}`;
  const ctaHtml = ctaText && ctaHref ? `
    <a href="${ctaHref}" class="btn btn-primary empty-state-cta">
      ${ctaIcon ? `<span class="cta-icon">${ctaIcon}</span>` : ''}
      ${ctaText}
    </a>
  ` : ctaText ? `
    <button class="btn btn-primary empty-state-cta">
      ${ctaIcon ? `<span class="cta-icon">${ctaIcon}</span>` : ''}
      ${ctaText}
    </button>
  ` : '';

  return `
    <div class="empty-state ${variantClass} ${className}">
      <div class="empty-state-icon">${icon}</div>
      <h3 class="empty-state-title">${title}</h3>
      <p class="empty-state-message">${message}</p>
      ${ctaHtml}
    </div>
  `;
}

/**
 * Render preset empty state
 * @param {string} preset - Preset name from EMPTY_STATE_PRESETS
 * @param {Object} overrides - Override options
 * @returns {string} - HTML string
 */
export function renderPresetEmptyState(preset, overrides = {}) {
  const config = EMPTY_STATE_PRESETS[preset];
  if (!config) {
    console.warn(`[EmptyState] Unknown preset: ${preset}`);
    return renderEmptyState(overrides);
  }
  return renderEmptyState({ ...config, ...overrides });
}

// ============================================================================
// WINDOW EXPORT (for non-module scripts)
// ============================================================================

if (typeof window !== 'undefined') {
  window.EmptyState = {
    render: renderEmptyState,
    renderPreset: renderPresetEmptyState,
    PRESETS: EMPTY_STATE_PRESETS
  };
}
